/**
 * Leviathan Web - Truck Compliance Section
 * Quasar Prime LLC
 * Tactical Industrial Design
 *
 * Manages compliance documents for a truck (DOT, insurance certs, IFTA, IRP, etc.)
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/config/api';
import { readApiFailure } from '@/utils/formErrors';

interface ComplianceDocument {
  id: string;
  truckId: string;
  documentType: string;
  documentUrl: string | null;
  issueDate: string | null;
  expirationDate: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface FormData {
  documentType: string;
  documentUrl: string;
  issueDate: string;
  expirationDate: string;
  notes: string;
}

const DOCUMENT_TYPES = [
  'DOT_INSPECTION',
  'INSURANCE_CERT',
  'REGISTRATION',
  'IFTA',
  'IRP',
  'HAZMAT_CERT',
  'WEIGHT_PERMIT',
  'OVERSIZE_PERMIT',
  'OTHER',
];

function formatDocType(type: string): string {
  return type.replace(/_/g, ' ');
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getExpirationStatus(dateStr: string | null): 'good' | 'warning' | 'expired' | 'none' {
  if (!dateStr) return 'none';
  const date = new Date(dateStr);
  const today = new Date();
  const thirtyDays = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

  if (date < today) return 'expired';
  if (date <= thirtyDays) return 'warning';
  return 'good';
}

function StatusBadge({ status }: { status: 'good' | 'warning' | 'expired' | 'none' }) {
  const styles = {
    good: 'text-success-green bg-success-green/10 border-success-green/30',
    warning: 'text-warning-amber bg-warning-amber/10 border-warning-amber/30',
    expired: 'text-error-red bg-error-red/10 border-error-red/30',
    none: 'text-text-tertiary bg-text-tertiary/10 border-text-tertiary/30',
  };

  const labels = {
    good: 'VALID',
    warning: 'EXPIRING',
    expired: 'EXPIRED',
    none: 'N/A',
  };

  return (
    <span
      className={`inline-block font-mono text-xs uppercase tracking-wider px-2 py-1 border ${styles[status]}`}
    >
      {labels[status]}
    </span>
  );
}

const emptyForm: FormData = {
  documentType: 'DOT_INSPECTION',
  documentUrl: '',
  issueDate: '',
  expirationDate: '',
  notes: '',
};

export default function TruckComplianceSection({ truckId }: { truckId: string }) {
  const { accessToken } = useAuth();
  const [documents, setDocuments] = useState<ComplianceDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDocuments();
  }, [truckId]);

  const fetchDocuments = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/trucks/${truckId}/compliance-documents`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents);
      }
    } catch (err) {
      console.error('Failed to fetch compliance documents:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEditModal = (doc: ComplianceDocument) => {
    setEditingId(doc.id);
    setFormData({
      documentType: doc.documentType,
      documentUrl: doc.documentUrl || '',
      issueDate: doc.issueDate ? doc.issueDate.split('T')[0] || '' : '',
      expirationDate: doc.expirationDate ? doc.expirationDate.split('T')[0] || '' : '',
      notes: doc.notes || '',
    });
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    setError('');
    setIsSaving(true);
    try {
      const payload = {
        documentType: formData.documentType,
        documentUrl: formData.documentUrl || null,
        issueDate: formData.issueDate || null,
        expirationDate: formData.expirationDate || null,
        notes: formData.notes || null,
      };

      const url = editingId
        ? `${API_URL}/api/trucks/${truckId}/compliance-documents/${editingId}`
        : `${API_URL}/api/trucks/${truckId}/compliance-documents`;

      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setShowModal(false);
        fetchDocuments();
      } else {
        /**
         * The API failure body is { success, error: { code, message, details } }
         * — there is no top-level `message`. `data.message || data.error`
         * therefore put the error OBJECT into React state, and rendering an
         * object as a child made React unmount the whole application to a
         * white page. `readApiFailure` always returns a string, and carries
         * the per-field issues alongside it.
         */
        const failure = await readApiFailure(response, 'Failed to save document');
        setError(failure.message);
      }
    } catch (err) {
      setError('Failed to save document');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (docId: string) => {
    try {
      const response = await fetch(
        `${API_URL}/api/trucks/${truckId}/compliance-documents/${docId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      if (response.ok) {
        setShowDeleteModal(null);
        fetchDocuments();
      }
    } catch (err) {
      console.error('Failed to delete document:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="text-text-tertiary font-mono animate-pulse">
          &gt; Loading compliance documents...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
          <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
            Compliance Documents
          </h3>
          <span className="text-text-tertiary font-mono text-sm ml-2">
            ({documents.length})
          </span>
        </div>
        <button
          onClick={openAddModal}
          className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-2 px-4 transition-all uppercase tracking-wider text-sm"
          style={{ boxShadow: '0 0 15px rgba(255, 149, 0, 0.3)' }}
        >
          + Add Document
        </button>
      </div>

      {/* Table */}
      {documents.length === 0 ? (
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber/30" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber/30" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber/30" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber/30" />
          <div className="bg-panel-dark border border-panel-border p-8 text-center">
            <p className="text-text-tertiary font-mono text-sm">
              &gt; No compliance documents on file
            </p>
          </div>
        </div>
      ) : (
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-panel-border bg-background/50">
                  <th className="text-left text-text-tertiary font-mono text-xs uppercase tracking-widest px-4 py-3">
                    Type
                  </th>
                  <th className="text-left text-text-tertiary font-mono text-xs uppercase tracking-widest px-4 py-3">
                    Document
                  </th>
                  <th className="text-left text-text-tertiary font-mono text-xs uppercase tracking-widest px-4 py-3">
                    Issue Date
                  </th>
                  <th className="text-left text-text-tertiary font-mono text-xs uppercase tracking-widest px-4 py-3">
                    Expiration
                  </th>
                  <th className="text-left text-text-tertiary font-mono text-xs uppercase tracking-widest px-4 py-3">
                    Status
                  </th>
                  <th className="text-right text-text-tertiary font-mono text-xs uppercase tracking-widest px-4 py-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => {
                  const status = getExpirationStatus(doc.expirationDate);
                  return (
                    <tr
                      key={doc.id}
                      className="border-b border-panel-border/50 hover:bg-amber/5 transition-colors"
                    >
                      <td className="px-4 py-3 font-mono text-sm text-text-primary">
                        {formatDocType(doc.documentType)}
                      </td>
                      <td className="px-4 py-3 font-mono text-sm text-text-secondary">
                        {doc.documentUrl ? (
                          <a
                            href={doc.documentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-steel hover:text-amber transition-colors underline"
                          >
                            View
                          </a>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-sm text-text-secondary">
                        {formatDate(doc.issueDate)}
                      </td>
                      <td className="px-4 py-3 font-mono text-sm text-text-secondary">
                        {formatDate(doc.expirationDate)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={status} />
                      </td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <button
                          onClick={() => openEditModal(doc)}
                          className="text-steel hover:text-amber font-mono text-xs uppercase tracking-wider transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setShowDeleteModal(doc.id)}
                          className="text-error-red/70 hover:text-error-red font-mono text-xs uppercase tracking-wider transition-colors"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-background/80 flex items-center justify-center z-50">
          <div className="relative stat-card max-w-lg w-full mx-4">
            <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
            <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

            <div className="bg-panel-dark border border-panel-border p-6">
              <h3 className="text-xl font-display text-text-primary uppercase tracking-wide mb-6">
                {editingId ? 'Edit Document' : 'Add Compliance Document'}
              </h3>

              {error && (
                <div className="p-3 bg-error-red/10 border border-error-red/50 mb-4">
                  <p className="text-error-red text-sm font-mono">&gt; {error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Document Type <span className="text-amber">*</span>
                  </label>
                  <select
                    value={formData.documentType}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, documentType: e.target.value }))
                    }
                    className="form-input w-full"
                  >
                    {DOCUMENT_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {formatDocType(type)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Document URL
                  </label>
                  <input
                    type="text"
                    value={formData.documentUrl}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, documentUrl: e.target.value }))
                    }
                    placeholder="https://..."
                    className="form-input w-full"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                      Issue Date
                    </label>
                    <input
                      type="date"
                      value={formData.issueDate}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, issueDate: e.target.value }))
                      }
                      className="form-input w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                      Expiration Date
                    </label>
                    <input
                      type="date"
                      value={formData.expirationDate}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, expirationDate: e.target.value }))
                      }
                      className="form-input w-full"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, notes: e.target.value }))
                    }
                    rows={3}
                    className="form-input w-full resize-none"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving || !formData.documentType}
                  className="px-4 py-2 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : editingId ? 'Update' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-background/80 flex items-center justify-center z-50">
          <div className="relative stat-card max-w-md w-full mx-4">
            <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-error-red" />
            <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-error-red" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-error-red" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-error-red" />

            <div className="bg-panel-dark border border-error-red/50 p-6">
              <h3 className="text-xl font-display text-error-red uppercase tracking-wide mb-4">
                Delete Document
              </h3>
              <p className="text-text-secondary font-mono text-sm mb-6">
                Are you sure you want to delete this compliance document? This action cannot be
                undone.
              </p>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(null)}
                  className="px-4 py-2 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(showDeleteModal)}
                  className="px-4 py-2 bg-error-red hover:bg-error-red/90 text-white font-display font-bold uppercase tracking-wider transition-all"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
